import React, { useEffect, useRef, useState } from 'react';
import './edit-data-modal.styles.scss';
import Modal, { ModalAlert, ModalConfirm } from '@/components/SHARED/modal/modal.component';
import ThreeDotsLoader from '@/components/SHARED/loader/three-dots-loader.component';
import flatpickr from 'flatpickr';
import { properCase } from '@/utils/properCase';
import { RandomString } from '@/utils/randomString';
import { useSelector } from 'react-redux';
import { UseCloudFunction } from '@/utils/firebase.utils';

const EditDataModal = ({
  show,
  cancel,
  dataPoint,
  users,
  mcList
}) => {

  const userProfile = useSelector(state => state.userData.userProfile);
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(false);
  const [activityTypes] = useState([
    'purchase-completed',
    'progress-started',
    'materials-accepted',
    'precheck-submitted',
    'precheck-declined',
    'precheck-approved',
    'portfolio-submitted',
    'portfolio-declined',
    'portfolio-approved'
  ]);
  const [alertMessage, setAlertMessage] = useState(null);
  const [confirmMessage, setConfirmMessage] = useState(null);
  const dataRef = useRef();
  const fp1Ref = useRef();
  const approveRef = useRef();
  const changesRef = useRef([]);

  useEffect(() => {
    if (show) return;
    console.log('destroying flatpickr')
    fp1Ref.current?.clear();
    fp1Ref.current?.destroy();
    fp1Ref.current = null;
    dataRef.current = {};
    changesRef.current = [];
    setFormData(dataRef.current);
  }, [show])

  useEffect(() => {
    if (!formData.ts && fp1Ref.current?.selectedDates.length > 0) {
      fp1Ref.current.destroy();
      fp1Ref.current = null;
      return;
    }
    const timeElement = document.getElementById('timestamp');
    if (!fp1Ref.current) {
      const flat1 = flatpickr(timeElement,
        {
          altInput: true,
          altFormat: "F j, Y, h:i K",
          enableTime: true,
          dateFormat: "F j, Y, h:i K",
          onChange: (dates) => {
            const date = new Date(dates[0]).getTime()
            saveData('ts', date)
          }
        }
      );
      fp1Ref.current = flat1;
      // console.log('set fp')
    }
    if (formData.ts && fp1Ref.current) {
      fp1Ref.current.setDate(new Date(formData.ts));
    }
  }, [formData])

  useEffect(() => {
    if (!mcList || !users) return;
    if (!dataPoint) {
      dataRef.current = {};
      setFormData(dataRef.current);
      return;
    }
    dataRef.current = { ...dataPoint };
    setFormData(dataRef.current);
  }, [dataPoint, mcList, users])

  function saveData(key, value) {
    if (dataRef.current[key] && value) {
      changesRef.current.push({
        'key': key,
        'value': value,
        'original': dataRef.current[key]
      })
    }
    dataRef.current = {
      ...dataRef.current,
      ...{ [key]: value }
    }
    setFormData(dataRef.current);
  }

  async function saveDataPointChanges() {
    let tempData = { ...dataRef.current };
    if (
      !tempData.cst_key ||
      !tempData.mc_key ||
      !tempData.type ||
      !tempData.ts
    ) {
      setAlertMessage('Please fill in all fields before saving.');
      return;
    }
    console.log(tempData);
    const user = users[tempData.cst_key];
    if (!user) {
      setAlertMessage('User not found. Please try again.');
      return;
    }
    setLoading(true);
    tempData.fName = user.fName;
    tempData.lName = user.lName;
    const rand = Math.ceil(Math.random() * 60000);
    const newTs = tempData.ts + rand;
    if (!tempData.id) {
      tempData.id = `${newTs}-${tempData.cst_key.substring(0, 8)}-${RandomString(8)}`;
      tempData.ts = newTs;
      tempData.createdBy = userProfile.cst_key;
      tempData.ts_created = new Date().getTime();
    } else {
      tempData.ts_edited = newTs;
      tempData[`edit_${newTs}`] = {
        'ts': newTs,
        'cst_key': userProfile.cst_key,
        'changes': changesRef.current
      }
    }
    if (!tempData.id.includes(tempData.ts.toString())) {
      tempData.ts = newTs;
    }
    // tempData.ts = newTs;
    tempData.progressId = `${tempData.mc_key}_${tempData.cst_key}`;
    tempData.state = user.state;
    console.log(tempData);
    const res = await UseCloudFunction(
      'saveDataPoint',
      {
        'dataPoint': tempData,
        'cst_key': userProfile.cst_key,
      }
    )
    console.log(res);
    setLoading(false);
    if (res.error) {
      setAlertMessage(`Something went wrong. Please try again later. (Error: ${res.error})`);
      return;
    }
    setAlertMessage('Data point saved successfully.');
    cancel();
  }

  function deleteDataPoint() {
    setConfirmMessage('Are you sure you want to delete this data point? This action cannot be undone.');
    approveRef.current = () => {
      continueDelete();
    }

    async function continueDelete() {
      setLoading(true);
      let tempData = { ...dataRef.current };
      console.log(tempData);
      const res = await UseCloudFunction(
        'saveDataPoint',
        {
          'dataPoint': tempData,
          'cst_key': userProfile.cst_key,
          'toDelete': true
        }
      )
      console.log(res);
      setLoading(false);
      if (res.error) {
        setAlertMessage(`Something went wrong. Please try again later. (Error: ${res.error})`);
        return;
      }
      setAlertMessage('Data point saved successfully.');
      cancel();
    }
  }

  return (
    <div className='edit-data-modal'>
      <Modal
        show={show}
        cancel={cancel}
        closeButton={true}
        text='Edit Data Point' >
        <div className='edit-data-modal-content'>
          {
            (formData) &&
            <div className='edit-data-form'>
              <div className='field disabled'>
                <label>User</label>
                <select
                  value={formData.cst_key ?? ''}
                  onChange={(e) => saveData('cst_key', e.target.value)} >
                  <option value=''>Choose a user...</option>
                  {
                    (users) &&
                    Object.values(users)
                      .sort((a, b) =>
                        (a.lName.toLowerCase() > b.lName.toLowerCase())
                          ? 1
                          : (a.lName.toLowerCase() === b.lName.toLowerCase())
                            ? (
                              (a.fName.toLowerCase() > b.fName.toLowerCase())
                                ? 1
                                : -1
                            )
                            : -1
                      )
                      .map((u) => (
                        <option key={u.cst_key} value={u.cst_key}>{u.lName}, {u.fName} ({u.cst_key})</option>
                      ))
                  }
                </select>
              </div>
              <div className='field disabled'>
                <label>Micro-Credential</label>
                <select
                  value={formData.mc_key ?? ''}
                  onChange={e => saveData('mc_key', e.target.value)} >
                  <option value=''>Choose a micro-credential...</option>
                  {
                    (mcList) &&
                    Object.values(mcList)
                      .sort((a, b) => a.name > b.name ? 1 : -1)
                      .map(m => {
                        return (
                          <option key={m.mc_key} value={m.mc_key}>{m.name}</option>
                        );
                      })
                  }
                </select>
              </div>
              <div className='field'>
                <label>Activity Type</label>
                <select
                  value={formData.type ?? ''}
                  onChange={(e) => saveData('type', e.target.value)}>
                  <option value=''>Choose a type...</option>
                  {
                    (activityTypes) &&
                    activityTypes.map(t => (
                      <option key={t} value={t}>{properCase(t.replace('-', ' '))}</option>
                    ))
                  }
                </select>
              </div>
              <div className='field'>
                <label>Date</label>
                <input className="flatpickr flatpickr-input" type="text" id="timestamp" />
              </div>
            </div>
          }
          <div className="g-space-0-5"></div>
          <div className='meta'>{dataPoint && dataPoint.id}</div>
          <hr />
          <div className='buttons with-left-button'>
            {
              (loading)
                ?
                <>
                  <div>
                    <button className='g-button small-btn red'>Delete</button>
                  </div>
                  <button className='g-button'>Cancel</button>
                  <button className='g-button'><ThreeDotsLoader /></button>
                </>
                :
                <>
                  <div>
                    <button
                      className='g-button small-btn red'
                      onClick={() => deleteDataPoint()}>
                      Delete Data Point
                    </button>
                  </div>
                  <button
                    className='g-button'
                    onClick={() => cancel()}>
                    Cancel
                  </button>
                  <button
                    className='g-button primary'
                    onClick={() => saveDataPointChanges()} >
                    Save Changes
                  </button>
                </>
            }
          </div>
        </div>
      </Modal>
      <ModalAlert
        show={alertMessage}
        cancel={() => setAlertMessage(null)}
        message={alertMessage} />
      <ModalConfirm
        show={confirmMessage}
        cancel={() => setConfirmMessage(null)}
        message={confirmMessage}
        onApprove={approveRef.current} />
    </div>
  )
}

export default EditDataModal